/**
 * Dynamic data binding for the prototype.
 *
 * Scans the root for `[data-bind="<path>"]` nodes and patches their text
 * with the resolved value from the source map. Sources are populated by
 * the `loadDynamicData` fetcher which queries:
 *   - /api/account/dashboard (auth required) → profile + learning
 *   - /api/site/metrics       (public)        → home metrics
 *   - /api/community/stats    (public)        → community stats
 *
 * Paths supported (flat namespace):
 *   profile.avatar / displayName / identity / planLabel / planDetail
 *   profile.streak / timeThisWeek / topicsExplored / articlesRemaining
 *   home.inProgress / thisWeek / bookmarks / votes
 *   community.ideasProduced / avgReviewTime
 *   community.ideasCount
 */
import type { Root } from "./prototypeTypes";
import type { PrototypeApiClient } from "./apiClient";
import type { AccountDashboardResponse, SiteMetricsResponse } from "./apiClientResponses";
import { isPaidPlan } from "./controllerState";
import { siteSettingsStore } from "./siteSettingsStore";
import { syncCommunityProofBadge } from './communityProofBadge';

export interface DynamicDataSource {
  profile?: {
    avatar: string;
    displayName: string;
    identity: string;
    planLabel: string;
    planDetail: string;
    articlesRemaining: string;
    streak: string;
    timeThisWeek: string;
    topicsExplored: string;
  } | null;
  home?: {
    inProgress: string;
    thisWeek: string;
    bookmarks: string;
    votes: string;
    structuredTopics: string;
    courseRating: string;
  } | null;
  community?: {
    ideasProduced: string | null;
    avgReviewTime: string | null;
    ideasCount: number;
  } | null;
  feedback?: {
    received: string;
    resolved: string;
    slaHours: string;
  } | null;
  siteSettings?: Record<string, string | number> | null;
}

/** Data-group names that loadDynamicDataForView can fetch */
export type DynamicDataGroup = "profile" | "home" | "community" | "feedback" | "siteSettings";

/**
 * Returns which dynamic data groups should be fetched for a given
 * view + plan combination.  Each group is only fetched when the
 * corresponding view is active, so the home page doesn't fire
 * /api/account/dashboard, /api/community/ideas?status=recent, or
 * /api/feedback/stats before the user navigates there.
 */
export function selectDynamicDataRequests(context: { view: string; plan: string }): DynamicDataGroup[] {
  switch (context.view) {
    case "home":
      // Paid users don't see the guest-metrics strip.
      return isPaidPlan(context.plan as Parameters<typeof isPaidPlan>[0]) ? [] : ["home"];
    case "profile":
      return ["profile"];
    case "community":
      return ["community"];
    case "feedback":
      return ["feedback"];
    case "ai-create":
      return ["siteSettings"];
    default:
      return [];
  }
}

async function fetchDashboard(api?: PrototypeApiClient): Promise<DynamicDataSource["profile"]> {
  try {
    const d: AccountDashboardResponse = api ? await api.getAccountDashboard() : await rawFetchJson("/api/account/dashboard");
    const u = d.user;
    const sub = d.subscription;
    const learn = d.learning;
    const initials = (u.displayName || u.email)
      .split(/[\s@.]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s: string) => s[0]?.toUpperCase() ?? "")
      .join("");
    const planLabel = sub.plan === "free" ? "Free Plan" : `${sub.plan.charAt(0).toUpperCase()}${sub.plan.slice(1)} Plan`;
    const planDetail = sub.plan === "free"
      ? `${learn.topicsExplored} topic${learn.topicsExplored === 1 ? "" : "s"} explored so far`
      : `Active until ${(sub.currentPeriodEnd || "").slice(0, 10)}`;
    return {
      avatar: initials || "·",
      displayName: u.displayName,
      identity: u.email,
      planLabel,
      planDetail,
      // articlesRemaining/streak/avgReviewTime are NOT fake-defaulted
      // when the API is missing. Bindings stay "—" (the markup
      // default) so a logged-in user never sees the prototype
      // "3 / 0 / —" values.
      articlesRemaining: "—",
      streak: "—",
      timeThisWeek: `${Math.round((learn.progressTotal ?? 0) / 60)} min`,
      topicsExplored: String(learn.topicsExplored ?? 0),
    };
  } catch {
    return null;
  }
}

async function fetchSiteMetrics(api?: PrototypeApiClient): Promise<DynamicDataSource["home"]> {
  try {
    const d: SiteMetricsResponse = api ? await api.siteMetrics() : await rawFetchJson("/api/site/metrics");
    const learners = d.activeLearners ?? 0;
    const topics = d.structuredTopics ?? 0;
    return {
      inProgress: String(learners),
      thisWeek: String(learners),
      bookmarks: String(topics),
      votes: '',
      structuredTopics: topics.toLocaleString(),
      courseRating: "—",
    };
  } catch {
    return null;
  }
}

async function fetchCommunityStats(api?: PrototypeApiClient): Promise<DynamicDataSource["community"]> {
  try {
    const d = api
      ? await api.communityStats()
		: await rawFetchJson("/api/community/stats") as { ideasProduced: number | null; avgReviewTime: string | null };
    return {
      ideasProduced: d.ideasProduced != null ? String(d.ideasProduced) : null,
      avgReviewTime: d.avgReviewTime ?? null,
      ideasCount: 0,
    };
  } catch {
    return null;
  }
}

function applyBindings(root: Root, data: DynamicDataSource) {
  const nodes = root.querySelectorAll<HTMLElement>("[data-bind]");
  nodes.forEach((node) => {
    const path = node.getAttribute("data-bind") ?? "";
    const [group, key] = path.split(".");
    const groupData = (data as Record<string, Record<string, unknown> | null>)[group];
    if (groupData && key && groupData[key] !== undefined && groupData[key] !== null) {
      node.textContent = String(groupData[key]);
    }
  });
}

async function fetchFeedbackStats(api?: PrototypeApiClient): Promise<DynamicDataSource["feedback"]> {
  try {
    const d = api
      ? await api.feedbackStats()
      : await rawFetchJson("/api/feedback/stats") as { received: number | null; resolved: number | null; reviewSlaHours: number };
    return {
      received: d.received != null ? d.received.toLocaleString() : "—",
      resolved: d.resolved != null ? d.resolved.toLocaleString() : "—",
      slaHours: String(d.reviewSlaHours ?? 48),
    };
  } catch {
    return null;
  }
}

/** Reads the boot-loaded site settings synchronously (no fetch needed). */
function fetchSiteSettings(): Record<string, string | number> | null {
  try {
    const s = siteSettingsStore.get();
    return {
      socialProofPrivateTopics: s.socialProofPrivateTopics,
      socialProofCompletionRate: s.socialProofCompletionRate,
      aiCreateCreditLabel: s.aiCreateCreditLabel,
    };
  } catch {
    return null;
  }
}

/** Fallback raw fetch for when no API client is injected. */
async function rawFetchJson(path: string): Promise<any> {
  const r = await fetch(path, { credentials: "include" });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}

// Generation guard: when the user switches views rapidly, an in-flight
// data load for the previous view could resolve and overwrite the new
// view's bindings. Each call to loadDynamicDataForView increments this
// counter; stale results check their generation before applying.
let dynamicDataGeneration = 0;

/** Prevents an off-screen Home metrics response from changing a return anchor. */
export function deactivateDynamicDataBindings(): void {
	dynamicDataGeneration += 1;
}

export async function loadDynamicDataForView(
  root: Root,
  context: { view: string; plan: string },
  api?: PrototypeApiClient,
): Promise<void> {
  const groups = selectDynamicDataRequests(context);
  if (groups.length === 0) return;

  const generation = ++dynamicDataGeneration;
  const data: DynamicDataSource = {};
  await Promise.all(groups.map(async (group) => {
    if (group === "profile") data.profile = await fetchDashboard(api);
    if (group === "home") data.home = await fetchSiteMetrics(api);
    if (group === "community") data.community = await fetchCommunityStats(api);
    if (group === "feedback") data.feedback = await fetchFeedbackStats(api);
    if (group === "siteSettings") data.siteSettings = fetchSiteSettings();
  }));
  // A newer view switch may have started while we were awaiting — discard.
  if (generation !== dynamicDataGeneration) return;
  applyBindings(root, data);
  if (data.community) syncCommunityProofBadge(root, data.community);
}
